'use client';

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/components/ui/utils';

interface FAQ {
  id: number;
  faqTitle: string;
  faqContent: string;
}

interface ProductsFaqProps {
  faqs?: FAQ[];
}

export default function ProductsFaq({ faqs }: ProductsFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const displayFaqs = useMemo(
    () =>
      (faqs ?? []).map((faq) => ({
        id: faq.id,
        q: faq.faqTitle,
        a: faq.faqContent,
      })),
    [faqs],
  );

  useEffect(() => {
    const targetId = window.location.hash.replace('#faq-', '');
    if (!targetId) return;

    const targetIndex = displayFaqs.findIndex((faq) => String(faq.id) === targetId);
    if (targetIndex === -1) return;

    setOpenIndex(targetIndex);
    window.setTimeout(() => {
      document.getElementById(`faq-${targetId}`)?.scrollIntoView({ block: 'center' });
    }, 100);
  }, [displayFaqs]);

  // Render strictly from CMS data. No hardcoded English fallback — the
  // parent (`FaqEnquirySection`) collapses the layout to a single-column
  // enquiry panel when CMS has no FAQs for the current locale.
  if (displayFaqs.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {displayFaqs.map((f, idx) => {
        const isOpen = openIndex === idx;
        return (
          <div
            key={f.id}
            id={`faq-${f.id}`}
            className={cn(
              'scroll-mt-24 overflow-hidden rounded-xl transition-colors duration-300',
              isOpen
                ? 'bg-[linear-gradient(135deg,rgba(237,28,37,0.08)_0%,rgba(20,20,20,0.95)_45%,rgba(12,12,12,1)_100%)] ring-1 ring-[#ed1c25]/15'
                : 'bg-[#0a0a0a]',
            )}
          >
            <button
              type="button"
              className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left sm:items-center sm:gap-3 sm:px-3.5"
              aria-expanded={isOpen}
              aria-controls={`faq-panel-${f.id}`}
              onClick={() => setOpenIndex(isOpen ? null : idx)}
            >
              <span
                className={cn(
                  'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold tabular-nums sm:mt-0',
                  isOpen ? 'bg-[#ed1c25] text-white' : 'bg-[#121212] text-white/80',
                )}
              >
                {String(idx + 1).padStart(2, '0')}
              </span>

              <span className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-white break-words">
                {f.q}
              </span>

              <span
                className={cn(
                  'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300 sm:mt-0',
                  isOpen ? 'bg-[#ed1c25] text-white' : 'bg-[#333333] text-white/70',
                )}
              >
                <ChevronLeft
                  className={cn(
                    'h-3.5 w-3.5 transition-transform duration-300',
                    isOpen ? 'rotate-90' : '-rotate-90',
                  )}
                  aria-hidden
                />
              </span>
            </button>

            <div
              id={`faq-panel-${f.id}`}
              className={cn(
                'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              )}
            >
              <div className="min-h-0 overflow-hidden">
                <div
                  className="px-3 pb-2.5 pl-[2.75rem] text-xs leading-relaxed text-white/70 sm:px-3.5 sm:pl-[3rem] [&_a]:text-[#ed1c25] [&_li]:text-white/70 [&_p]:text-white/70 [&_strong]:font-semibold [&_strong]:text-white"
                  dangerouslySetInnerHTML={{ __html: f.a }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
